import { getAuth } from 'firebase/auth';

// Use /api prefix for proxy routing in development, direct URL in production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://rateiq-production.up.railway.app')
  : '/api';

class ApiService {
  async getAuthHeaders() {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      const token = await user.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
    
    return {
      'Content-Type': 'application/json',
    };
  }

  // Simple status check - perfect first endpoint to test
  async checkStatus() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_BASE_URL}/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status} ${response.statusText}`);
      }
      
      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out - backend server may be unreachable');
      }
      throw new Error(`Network error: ${error.message}`);
    }
  }

  // Get current user info (requires auth)
  async getCurrentUser() {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/whoami`, {
      method: 'GET',
      headers: headers,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get current user');
    }
    
    return response.json();
  }

  // Try different possible upload endpoints
  async uploadPdf(formDataOrFile) {
    let formData;
    
    // Handle both FormData object and File object
    if (formDataOrFile instanceof FormData) {
      formData = formDataOrFile;
    } else {
      formData = new FormData();
      formData.append('file', formDataOrFile);
    }
    
    const auth = getAuth();
    const user = auth.currentUser;
    let headers = {};
    
    if (user) {
      const token = await user.getIdToken();
      headers = {
        'Authorization': `Bearer ${token}`,
      };
    }
    
    // Try common upload endpoints in order
    const endpoints = ['/upload-pdf', '/upload', '/api/upload-pdf', '/api/upload'];
    let lastError;
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying upload endpoint: ${endpoint}`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
        
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: headers,
          body: formData,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          console.log(`Success with endpoint: ${endpoint}`);
          return response.json();
        } else if (response.status !== 404) {
          // If it's not a 404, this might be the right endpoint but with a different error
          const error = await response.json().catch(() => ({ message: `HTTP ${response.status}: ${response.statusText}` }));
          throw new Error(error.message || 'Failed to upload PDF');
        }
        
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Upload timed out - please check your connection and try again');
        }
        lastError = error;
        console.log(`Endpoint ${endpoint} failed:`, error.message);
        continue; // Try next endpoint
      }
    }
    
    // If all endpoints failed
    throw new Error(`Upload failed - no valid endpoint found. Last error: ${lastError?.message || 'Unknown error'}`);
  }
  
  // Get list of user's statements
  async getStatements() {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/statements`, {
      method: 'GET',
      headers: headers,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get statements');
    }
    
    return response.json();
  }
  
  // Fixed: Backend uses /statement/{pdf_id}, not /statements/{statement_id}
  async getStatement(pdfId) {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/statement/${pdfId}`, {
      method: 'GET',
      headers: headers,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get statement');
    }
    
    return response.json();
  }
  
  // Get metrics for a specific statement
  async getMetrics(pdfId) {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}/metrics/${pdfId}`, {
      method: 'GET',
      headers: headers,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get metrics');
    }
    
    return response.json();
  }


}

const apiService = new ApiService();
export default apiService; 